import type { dynamicObj } from 'src/pages/assets';
import { useUserStore } from 'src/store/main';
import { SessionStorage, LocalStorage } from 'quasar';
import { getUserDetailFromkey } from 'src/utils/UserDetails';
import { getOrgId } from 'src/utils/organization/org_id_helper';

const default_expiry = 28800000;

export function setWithExpiry(key: string, value: any, user_settings = false) {
  const now = getExpireTime();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now,
    user_settings: user_settings,
  };
  SessionStorage.set(key, JSON.stringify(item));
}

export function getWithExpiry(key: string, user_settings = false) {
  if (!SessionStorage.has(key)) {
    return null;
  }
  const itemStr = SessionStorage.getItem(key) as string;
  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }

  const item = JSON.parse(itemStr) as dynamicObj;
  const now = getExpireTime();
  // compare the expiry time of the item with the current time
  if (now > item.expiry || (user_settings && !item.user_settings)) {
    // If the item is expired, delete the item from storage
    // and return null
    SessionStorage.remove(key);
    return null;
  }
   
  return item.value;
}

export function isUserSetting(key: string) {
  if (!SessionStorage.has(key)) {
    return false;
  }
  const itemStr = SessionStorage.getItem(key) as string;
  if (!itemStr) {
    return false;
  }

  const item = JSON.parse(itemStr) as dynamicObj;
  if (item.user_settings) {
    return true;
  }

  return false;
}

function getExpireTime() {
  return new Date().setHours(2, 0, 0, 0);
}

export function getCustomFieldStorageName(doc_type: string) {
  return 'custom_field_' + doc_type;
}

function addUserIDInKey(key: string) {
  const user_id = getUserDetailFromkey('id');
  if (!user_id) {
    return key;
  }

  return user_id + '_' + key;
}

export function getUserBasedKey(key = '', user_only = true) {
  if (!key || key == '') {
    return key;
  }

  const user_filter_key = addUserIDInKey(key);
  // for already saved
  if (user_only || LocalStorage.has(user_filter_key)) {
    key = user_filter_key;
  }
  return key;
}

export async function getOrgBasedKey(key = '') {
  if (!key || key == '') {
    return key;
  }

  const user_store = useUserStore();
  const { getSubOrgId } = user_store;

  const sub_org_base_key = await getSubOrgId();

  if (sub_org_base_key) {
    return sub_org_base_key + '_' + key;
  }

  const org_base_key = getOrgId();

  return org_base_key + '_' + key;
}

export function removeUserBasedKey(key: string) {
  key = addUserIDInKey(key);
  LocalStorage.remove(key);
}
